'use client'
import {useRef} from 'react'
import {grid, gridItem} from '~/design-system/foundations'
import {HgHeading} from '~/design-system/hg/components'
import {cn} from '~/design-system/utils'
import type {HgHeroCinematicProps} from './types'
import CinematicVariant from '../CinematicVariant'
import {Disclaimer} from '../Disclaimer'
import type {HgHeroBaseProps} from '../../types'
import {getHeadingContentTypeProps} from '../../utils'
import {cinematicHeroVariants} from './variants'
import styles from './styles.module.css'

export type HgCinematicHeroProps = Omit<HgHeroBaseProps, 'alignment'> &
  HgHeroCinematicProps

export default function CinematicHero(props: HgCinematicHeroProps) {
  const {headline, disclaimer, subhead, classNames, breadcrumbs} = props
  const headingContainerRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const {heading, headingContainer, gridSlot, disclaimerSlot, container} =
    cinematicHeroVariants(props)

  return (
    <div
      className={cn(container({className: styles.container}), classNames?.container)}
      style={
        {
          '--background': props.variant === 'stacked' ? props.backgroundColor : '',
        } as React.CSSProperties
      }
      onPointerMove={event => {
        if (!props.maskImage || !headingContainerRef.current) return
        const offset =
          props.variant === 'stacked' ? headingContainerRef.current.scrollHeight : 0

        updateHoverEffectCoordinates(event, containerRef.current, offset)
      }}
      ref={containerRef}
      data-apply-grain={props.variant === 'stacked' && props.applyGrain}
    >
      <CinematicVariant {...props} />
      <div
        className={grid({
          className: gridSlot(),
        })}
      >
        <div
          className={gridItem({
            size: 'heading',
            className: headingContainer(),
          })}
          ref={headingContainerRef}
        >
          <HgHeading
            {...getHeadingContentTypeProps({
              props,
              buttonVariant: 'frosted',
            })}
            variation="hero"
            topSpacing={props.variant === 'overlay' ? 's0' : 's8'}
            breadcrumbs={breadcrumbs}
            className={heading()}
            headline={headline}
            subhead={subhead}
            // TODO: Update when all old cinematic heros are updated
            alignment={props.variant !== 'stacked' ? props.alignment : 'center'}
          />
        </div>
      </div>
      <Disclaimer disclaimer={disclaimer} className={disclaimerSlot()} />
    </div>
  )
}

// Updates CSS custom properties for overlay hover effect positioning
function updateHoverEffectCoordinates(
  event: React.PointerEvent,
  container: HTMLDivElement | null,
  offset: number
) {
  if (!container) return

  const rect = container.getBoundingClientRect()
  const x = event.clientX - rect.left
  const y = event.clientY - rect.top - offset

  container.style.setProperty('--x', `${x}px`)
  container.style.setProperty('--y', `${y}px`)
}
